import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { api } from "@/api/api";
import {
  PurchaseStatus,
  type PaymentMethod,
  type PaymentResult,
} from "@/app/payments/types";
import { platformApi } from "@/app/platform";
import { useCheckoutPayment } from "@/hooks/useCheckoutPayment";
import { FlightTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import type { TemporallyStoreFlightBookingDetailsBodyInputDto } from "@/types/codegen";
import { updatePersonalData } from "@/utils/bookings";

type PaymentParams = TemporallyStoreFlightBookingDetailsBodyInputDto & {
  offerId: string;
  promoCode?: string;
};

type BnplPaymentParams = PaymentParams & {
  sessionId: string;
  otp: string;
};

export type FlightAnalyticsData = {
  origin?: string;
  destination?: string;
  departureDate?: string;
  travellersCount?: number;
  totalPrice?: number;
  currency?: string;
  searchResultKey?: string;
};

const buildSuccessPayload = (
  paymentMethod: string,
  transactionId: string,
  analyticsData?: FlightAnalyticsData,
) => ({
  payment_method: paymentMethod,
  payment_status: "Success" as const,
  transaction_id: transactionId,
  origin: analyticsData?.origin,
  destination: analyticsData?.destination,
  departure_date: analyticsData?.departureDate,
  travellers_count: analyticsData?.travellersCount,
  total_price: analyticsData?.totalPrice,
  currency: analyticsData?.currency,
  search_result_key: analyticsData?.searchResultKey,
});

type Props = {
  onSuccess: (result: PaymentResult) => void;
  initialAvailabilityToken?: string;
  method?: PaymentMethod;
  analyticsData?: FlightAnalyticsData;
};

export const useFlightBooking = ({
  onSuccess,
  initialAvailabilityToken,
  method,
  analyticsData,
}: Props) => {
  const [checkoutPurchasePending, setCheckoutPurchasePending] = useState(false);

  const { flowComponent, isLoadingForm } = useCheckoutPayment({
    availabilityToken: initialAvailabilityToken,
    serviceType: "air",
    enabled: method === "checkout",
    onSuccess: (result) => {
      if (result?.status === PurchaseStatus.SUCCESSFUL) {
        rudderAnalytics.track(
          FlightTrackingEvents.USER_SUCCESSFULY_PAID,
          buildSuccessPayload("checkout", result.orderId, analyticsData),
        );
      }
      onSuccess(result);
    },
    onSubmit: () => setCheckoutPurchasePending(true),
  });

  const handleError = useCallback((error: unknown) => {
    console.error("Error in booking or payment process:", error);
    Sentry.captureException(error);
  }, []);

  const checkoutPayment = {
    // TODO: promoCode is not being passed to order creation, checkout payment must be refactored before it's possible
    submit: async ({ offerId, promoCode, ...postData }: PaymentParams) => {
      try {
        if (!flowComponent) {
          return;
        }

        const wasSuccess = await updatePersonalData();
        if (!wasSuccess) {
          return {
            orderId: "",
            status: PurchaseStatus.CANCELLED,
          };
        }

        await api.post(
          `/flights/${offerId}/book`,
          postData satisfies TemporallyStoreFlightBookingDetailsBodyInputDto,
        );
        flowComponent.submit();
      } catch (error) {
        handleError(error);
        setCheckoutPurchasePending(false);
      }
    },
    isLoadingCheckoutForm: isLoadingForm,
    isPending: checkoutPurchasePending,
  };

  const qiPayment = useMutation({
    mutationFn: async ({ offerId, promoCode, ...postData }: PaymentParams) => {
      const wasSuccess = await updatePersonalData();
      if (!wasSuccess) {
        return {
          orderId: "",
          status: PurchaseStatus.CANCELLED,
        };
      }

      await api.post(
        `/flights/${offerId}/book`,
        postData satisfies TemporallyStoreFlightBookingDetailsBodyInputDto,
      );

      // Use standard payment flow
      return platformApi.getPaymentProvider("standard").initiatePayment({
        availabilityTokens: [postData.availabilityToken],
        serviceType: "air",
        promoCode,
        onSuccess: ({ orderId }) => {
          rudderAnalytics.track(
            FlightTrackingEvents.USER_SUCCESSFULY_PAID,
            buildSuccessPayload("qiAccount", orderId, analyticsData),
          );
        },
        onFailure: ({ orderId }) => {
          rudderAnalytics.track(FlightTrackingEvents.USER_UNSUCCESSFULY_PAID, {
            payment_method: "qiAccount",
            payment_status: "fail",
            transaction_id: orderId,
          });
        },
      });
    },
    onSuccess,
    onError: handleError,
  });

  const bnplPayment = useMutation({
    mutationFn: async ({
      offerId,
      sessionId,
      otp,
      promoCode,
      ...postData
    }: BnplPaymentParams) => {
      const wasSuccess = await updatePersonalData();
      if (!wasSuccess) {
        return {
          orderId: "",
          status: PurchaseStatus.CANCELLED,
        };
      }

      await api.post(
        `/flights/${offerId}/book`,
        postData satisfies TemporallyStoreFlightBookingDetailsBodyInputDto,
      );

      return platformApi.getPaymentProvider("bnpl").initiatePayment({
        availabilityTokens: [postData.availabilityToken],
        sessionId,
        otp,
        promoCode,
      });
    },
    onSuccess: (result) => {
      if (result?.status === PurchaseStatus.SUCCESSFUL) {
        rudderAnalytics.track(
          FlightTrackingEvents.USER_SUCCESSFULY_PAID,
          buildSuccessPayload("bnpl", result.orderId, analyticsData),
        );
      }
      onSuccess(result);
    },
    onError: handleError,
  });

  return {
    qiPayment,
    checkoutPayment,
    bnplPayment,
  };
};
